import { createFeedbackSchema, type FeedbackKind } from '@nexo/types';
import { Button, Field, Input, Modal, SegmentedControl, Textarea, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bug, Lightbulb } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { errorMessage, type FieldErrors, validate } from '../../lib/forms';
import { isDesktop } from '../../lib/platform';

export const myFeedbackKey = ['feedback', 'mine'] as const;

const copy: Record<FeedbackKind, { title: string; description: string }> = {
  bug: {
    title: '¿Qué ha fallado?',
    description: 'Qué estabas haciendo, qué esperabas que pasara y qué ha pasado en realidad.',
  },
  idea: {
    title: '¿Qué te gustaría?',
    description: 'Cuéntanos la idea y para qué te vendría bien.',
  },
};

/** Lets anyone report an error or suggest an improvement to the Nexo team. */
export function FeedbackDialog({
  open,
  onClose,
  initialKind = 'bug',
}: {
  open: boolean;
  onClose: () => void;
  initialKind?: FeedbackKind;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<FeedbackKind>(initialKind);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const send = useMutation({
    mutationFn: api.feedback.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myFeedbackKey });
      toast({
        title: kind === 'bug' ? t('Gracias por avisarnos') : t('Gracias por la idea'),
        description: t('Puedes seguir su estado en tu perfil.'),
        tone: 'success',
      });
      setTitle('');
      setDescription('');
      onClose();
    },
    onError: (err) => setErrors({ _: errorMessage(err) }),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const input = {
      kind,
      title,
      description,
      // Helps reproduce errors; shown to the person below the form.
      context: {
        appVersion: __APP_VERSION__,
        platform: isDesktop ? 'desktop' : 'web',
        path: window.location.pathname,
        userAgent: navigator.userAgent.slice(0, 500),
      },
    };
    const result = validate(createFeedbackSchema, input);
    if (result.errors) return setErrors(result.errors);
    send.mutate(input);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Reportar un error o sugerir una mejora')}
      description={t('Lo recibe el equipo de Nexo.')}
      size="md"
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <SegmentedControl<FeedbackKind>
          label={t('Tipo')}
          value={kind}
          onChange={(k) => {
            setKind(k);
            setErrors({});
          }}
          options={[
            { value: 'bug', label: t('Algo no funciona'), icon: <Bug /> },
            { value: 'idea', label: t('Tengo una idea'), icon: <Lightbulb /> },
          ]}
        />
        <Field label={t(copy[kind].title)} error={errors.title}>
          {(props) => (
            <Input
              {...props}
              autoFocus
              maxLength={120}
              placeholder={
                kind === 'bug' ? t('Ej.: el tablero no carga') : t('Ej.: modo compacto en el chat')
              }
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setErrors({});
              }}
            />
          )}
        </Field>
        <Field label={t('Detalles')} error={errors.description} hint={t(copy[kind].description)}>
          {(props) => (
            <Textarea
              {...props}
              rows={5}
              maxLength={5000}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors({});
              }}
            />
          )}
        </Field>
        <p className="text-xs text-subtle">
          {t(
            'Se adjuntan la versión ({{platform}} v{{version}}), tu navegador y la pantalla en la que estás, para poder reproducirlo.',
            {
              platform: isDesktop ? t('app de escritorio') : t('web'),
              version: __APP_VERSION__,
            },
          )}
        </p>
        {errors._ && (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            {errors._}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <Button type="submit" loading={send.isPending}>
            {t('Enviar')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
